import React, { useState } from 'react';
import { Card, Label, Icon, Divider, Popup, Progress } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { AvailableCourtSlotDTO, TimeSlot } from '../../../app/models/AvailableCourtSlots';

interface Props {
    slot: AvailableCourtSlotDTO;
    onSlotSelect?: (slot: AvailableCourtSlotDTO, timeSlot: TimeSlot) => void;
}

// Ensures URLs without a protocol (e.g. "www.sportizan.com") get https:// prepended
// so the browser treats them as absolute URLs instead of relative paths.
function ensureUrl(url: string | null | undefined): string | null {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
}

// Returns a Semantic UI colour name based on remaining capacity percentage
function capacityColor(pct: number): 'green' | 'yellow' | 'orange' | 'red' {
    if (pct >= 75) return 'green';
    if (pct >= 50) return 'yellow';
    if (pct >= 25) return 'orange';
    return 'red';
}

export default function CourtListItem({ slot, onSlotSelect }: Props) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [selectedTime, setSelectedTime] = useState<TimeSlot | null>(null);

    const handleTimeSelect = (ts: TimeSlot) => {
        setSelectedTime(ts);
        onSlotSelect?.(slot, ts);
    };

    const fullAddress = [slot.venueAddress, slot.venueCity, slot.venuePostalCode]
        .filter(Boolean)
        .join(', ');

    const websiteUrl = ensureUrl(slot.venueWebsite);
    const youTubeUrl = ensureUrl(slot.venueYouTubeLink);

    const displayedSlots = expanded
        ? slot.availableSlots
        : slot.availableSlots.slice(0, 6);

    // A slot is "partial" when some capacity is used but not all
    const isPartial = (ts: TimeSlot) =>
        ts.totalCapacity > 0 && ts.remainingCapacity < ts.totalCapacity;

    return (
        <Card fluid style={{ marginBottom: '10px' }}>
            <Card.Content>

                {/* ── Header ────────────────────────────────────────────────── */}
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
                    <div>
                        <Card.Header style={{ fontSize: '1rem' }}>{slot.companyName}</Card.Header>
                        <Card.Meta style={{ fontSize: '0.85rem' }}>
                            <strong>{slot.venueName}</strong>
                            {slot.assetName && <> &bull; {slot.assetName}</>}
                        </Card.Meta>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <Label size='small' color='grey'>
                            <Icon name='map marker alternate' />
                            {slot.distanceKm} {t('courts.list.item.kmAway')}
                        </Label>
                        {slot.defaultPricePerHour != null ? (
                            <Label size='small' color='green'>
                                <Icon name='money bill alternate outline' />
                                ${slot.defaultPricePerHour}/{t('courts.list.item.hr')}
                            </Label>
                        ) : (
                            <Label size='small' basic>{t('courts.list.item.contactVenue')}</Label>
                        )}
                    </div>
                </div>

                <Divider style={{ margin: '8px 0' }} />

                {/* ── Address ───────────────────────────────────────────────── */}
                {fullAddress && (
                    <div style={{ fontSize: '0.82rem', color: '#555', marginBottom: '6px' }}>
                        <Icon name='map marker alternate' color='grey' />
                        {fullAddress}
                    </div>
                )}

                {/* ── Description ───────────────────────────────────────────── */}
                {slot.venueDescription && (
                    <Card.Description style={{ fontSize: '0.82rem', color: '#555', marginBottom: '4px' }}>
                        {slot.venueDescription}
                    </Card.Description>
                )}

                {/* ── Asset description ─────────────────────────────────────── */}
                {slot.assetDescription && (
                    <Card.Description style={{ fontSize: '0.82rem', color: '#444', marginBottom: '8px', fontStyle: 'italic' }}>
                        <Icon name='info circle' color='grey' size='small' />
                        {slot.assetDescription}
                    </Card.Description>
                )}

                {/* ── Supported sports ──────────────────────────────────────── */}
                {slot.supportedSports?.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 'bold', marginRight: '6px' }}>
                            <Icon name='futbol' />
                            {t('courts.list.item.supportedSports')}:
                        </span>
                        <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px' }}>
                            {slot.supportedSports.map(sport => (
                                <Label key={sport.sportID} size='mini' color='teal'>
                                    {sport.sportName}
                                </Label>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Available time slots ──────────────────────────────────── */}
                <div style={{ marginBottom: '6px' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 'bold', marginRight: '6px' }}>
                        <Icon name='clock outline' />
                        {t('courts.list.item.availableTimes')}:
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                        {displayedSlots.map((ts, idx) => {
                            const partial  = isPartial(ts);
                            const color    = partial ? capacityColor(ts.capacityPercent) : 'blue';
                            const selected = selectedTime?.startTime === ts.startTime;

                            return (
                                <Popup
                                    key={idx}
                                    disabled={!partial}
                                    position='top center'
                                    size='mini'
                                    trigger={
                                        <div
                                            onClick={() => handleTimeSelect(ts)}
                                            style={{ cursor: 'pointer', minWidth: '90px' }}
                                        >
                                            {/* Time label */}
                                            <Label
                                                color={selected ? 'teal' : color}
                                                size='small'
                                                style={{ width: '100%', textAlign: 'center', marginBottom: '3px' }}
                                            >
                                                {ts.startTime} – {ts.endTime}
                                                {partial && (
                                                    <span style={{ marginLeft: '4px', opacity: 0.85 }}>
                                                        ({ts.capacityPercent}%)
                                                    </span>
                                                )}
                                            </Label>
                                            {/* Capacity progress bar — only shown for partial slots */}
                                            {partial && (
                                                <Progress
                                                    percent={ts.capacityPercent}
                                                    size='tiny'
                                                    color={color}
                                                    style={{ margin: 0, borderRadius: '2px' }}
                                                />
                                            )}
                                        </div>
                                    }
                                    content={t('courts.list.item.capacityTooltip', {
                                        remaining: ts.remainingCapacity,
                                        total:     ts.totalCapacity,
                                        percent:   ts.capacityPercent,
                                    })}
                                />
                            );
                        })}

                        {slot.availableSlots.length > 6 && (
                            <Label
                                as='a'
                                basic
                                size='small'
                                color='teal'
                                style={{ cursor: 'pointer', alignSelf: 'flex-start' }}
                                onClick={() => setExpanded(e => !e)}
                            >
                                {expanded
                                    ? t('courts.list.item.showLess')
                                    : t('courts.list.item.showMore', { count: slot.availableSlots.length - 6 })}
                            </Label>
                        )}
                    </div>
                </div>

                <Divider style={{ margin: '8px 0' }} />

                {/* ── Footer: phone + website + YouTube + directions ────────── */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {slot.venueContactPhone && (
                        <a href={`tel:${slot.venueContactPhone}`} style={{ fontSize: '0.82rem', color: '#555' }}>
                            <Icon name='phone' color='grey' />
                            {slot.venueContactPhone}
                        </a>
                    )}
                    {websiteUrl && (
                        <a href={websiteUrl} target='_blank' rel='noreferrer'
                            style={{ fontSize: '0.82rem', color: '#00b5ad' }}>
                            <Icon name='globe' />
                            {t('courts.list.item.website')}
                        </a>
                    )}
                    {youTubeUrl && (
                        <a href={youTubeUrl} target='_blank' rel='noreferrer'
                            style={{ fontSize: '0.82rem', color: '#db2828' }}>
                            <Icon name='youtube' />
                            {t('courts.list.item.video')}
                        </a>
                    )}
                    <Popup
                        trigger={
                            <a
                                href={`https://www.google.com/maps/dir/?api=1&destination=${slot.venueLatitude},${slot.venueLongitude}`}
                                target='_blank' rel='noreferrer'
                                style={{ fontSize: '0.82rem', color: '#2185d0' }}
                            >
                                <Icon name='map outline' />
                                {t('courts.list.item.directions')}
                            </a>
                        }
                        content={t('courts.list.item.openInMaps')}
                        size='mini'
                    />
                </div>

            </Card.Content>
        </Card>
    );
}